<template>
  <div
    v-if="isCompany && job && job.published && job.isJobPost"
    class="tw-w-full tw-bg-truegray-50 tw-rounded tw-border tw-border-solid tw-border-gray-200"
  >
    <WdsCollapse :open="false" :content-id="job.name">
      <template #title>
        <WdsStack direction-column class="tw-p-4">
          <WdsStack class="tw-w-full tw-flex-1 tw-whitespace-nowrap" gap-md justify-space-between>
            <WdsStack gap-sm class="tw-items-center">
              <WdsIcon name="link" :size="16" />
              <WdsText :text="$t('Sourced from job')" />
            </WdsStack>
          </WdsStack>
          <div class="tw-pt-2">
            <WdsText class="tw-justify-start tw-w-full">
              <a :href="laravelNamedRoute('jobs.show', { job: job.id })">
                {{ job.name }}
              </a>
            </WdsText>
          </div>
        </WdsStack>
      </template>
      <template #content>
        <WdsStack class="tw-w-full tw-px-4 tw-pb-4" direction-column>
          <WdsDivider class="tw-pb-4" />
          <WdsGrid row-gap-lg col-gap-lg style="grid-template-columns: 1fr 2fr">
            <WdsText v-if="job.association" class="tw-text-nowrap" :text="$t('Commitment')" />
            <WdsText v-if="job.association" :text="formattedAssociation(job.association)" />

            <WdsText class="tw-text-nowrap" :text="$t('Seniority')" />
            <WdsStack class="tw-flex tw-flex-wrap">
              <WdsText
                v-for="(experience, index) in job.expectedExperienceLevel"
                :key="experience"
                :text="`${convertEnumToDisplayText(experience)}${index < job.expectedExperienceLevel.length - 1 ? ',' : ''}`"
                :class="index < job.expectedExperienceLevel.length - 1 ? 'tw-pr-1' : ''"
              />
            </WdsStack>

            <WdsText :text="job.industries.length > 1 ? $t('Industries') : $t('Industry')" />
            <WdsStack class="tw-flex tw-flex-wrap">
              <WdsText
                v-for="(industry, index) in job.industries"
                :key="industry.name"
                :text="`${industry.name}${index < job.industries.length - 1 ? ',' : ''}`"
                :class="index < job.industries.length - 1 ? 'tw-pr-1' : ''"
              />
            </WdsStack>

            <WdsText :text="$t('Languages')" />
            <WdsStack direction-column gap-sm>
              <WdsText
                v-for="language in job.languages"
                :key="language.name"
                :text="
                  convertEnumToDisplayText(language.name) +
                  ' - ' +
                  convertEnumToDisplayText(language.experience)
                "
              />
            </WdsStack>

            <WdsText :text="$t('Skills')" />
            <WdsTagGroup :max-tags="4" :tags="jobSkills" />
          </WdsGrid>
        </WdsStack>
      </template>
    </WdsCollapse>
  </div>
</template>

<script lang="ts" setup>
import {
  WdsCollapse,
  WdsDivider,
  WdsGrid,
  WdsIcon,
  WdsStack,
  WdsTagGroup,
  WdsText,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { computed } from 'vue'
import { graphql, useFragment } from '@/gql'
import { HireJobFragment } from '@/components/Hire/graphql/fragments'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'
import { $t } from '@/composables/translation'
import { Association } from '@/gql/graphql'
import { convertEnumToDisplayText } from '@/utils/tools'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'

const { isCompany } = useAccountCapabilities()

const props = defineProps<{
  hireId: string
}>()

const { result } = useQuery(
  graphql(`
    query hireJob($id: ID!, $isCompany: Boolean!) {
      hire(id: $id) {
        id
        job {
          ...HireJobType
        }
      }
    }
  `),
  () => ({
    id: props.hireId,
    isCompany: isCompany.value,
  }),
  {
    clientId: 'publicApi',
  },
)

const job = computed(() => useFragment(HireJobFragment, result.value?.hire?.job))

const jobSkills = computed(() =>
  job.value?.skills.map((skill) => ({ label: skill.name, value: skill.name, primary: false })),
)

const formattedAssociation = (association: Association) => {
  switch (association) {
    case Association.FullTime:
      return $t('Full-Time Contractor')
    case Association.PartTime:
      return $t('Part-Time/Flexible Days')
    case Association.Project:
      return $t('One-Time Project')
    default:
      return $t('None')
  }
}
</script>

<style lang="scss" scoped></style>
